/**
 * WHS Head Quarters (HQ) & Geofencing Utilities
 * Defines static HQ coordinates, 50m radius geofence rule, and distance calculation.
 */

export const MAIN_HQ_CONFIG = {
  id: "16327",
  displayJobId: "16327",
  name: "Main Head Quaters",
  isMainSite: true,
  // Static Head Quarter Coordinates (Canberra ACT Office)
  latitude: 30.710227,
  longitude: 76.706521,
  radiusMeters: 50,
};

/**
 * Checks if a site/job represents the Main Head Quaters.
 */
export const isMainSite = (job) => {
  if (!job) return false;
  if (job.isMainSite === true || job.IsMainSite === true) return true;
  const id = (job.id || job.JobId || job.jobId || "").toString().toLowerCase();
  const displayId = (job.displayJobId || job.DisplayJobId || "").toString();
  const name = (job.name || job.Title || job.JobName || job.jobTitle || "").toLowerCase();
  return (
    displayId === "16327" ||
    id === "16327" ||
    name.includes("head quater") ||
    name.includes("headquarters") ||
    name.includes("main head")
  );
};

/**
 * Calculates Great-Circle distance between two points in meters using Haversine formula.
 */
export const calculateDistanceMeters = (lat1, lon1, lat2, lon2) => {
  if (
    lat1 === undefined || lat1 === null ||
    lon1 === undefined || lon1 === null ||
    lat2 === undefined || lat2 === null ||
    lon2 === undefined || lon2 === null
  ) {
    return null;
  }
  const R = 6371e3; // Earth radius in meters
  const toRad = (deg) => (Number(deg) * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c);
};

/**
 * Evaluates whether a set of user coordinates is within the allowed geofence of targetSite (default 50m).
 */
export const evaluateGeofence = (userCoords, targetSite = MAIN_HQ_CONFIG) => {
  if (!userCoords || userCoords.latitude === undefined || userCoords.longitude === undefined || (userCoords.latitude === 0 && userCoords.longitude === 0)) {
    return {
      isValidLocation: false,
      isWithinGeofence: false,
      distanceMeters: null,
      radiusMeters: targetSite.radiusMeters || 50,
      message: "GPS coordinates unavailable or permission denied",
    };
  }

  const distance = calculateDistanceMeters(
    userCoords.latitude,
    userCoords.longitude,
    targetSite.latitude,
    targetSite.longitude
  );

  const radius = targetSite.radiusMeters || 50;
  const isWithin = distance !== null && distance <= radius;

  return {
    isValidLocation: true,
    isWithinGeofence: isWithin,
    distanceMeters: distance,
    radiusMeters: radius,
    formattedDistance: distance !== null ? `${distance}m` : "--",
    message: isWithin
      ? `Within 50m boundary (${distance}m away)`
      : `Outside 50m boundary (${distance}m away — max 50m)`,
  };
};

/**
 * Resolves the real geofence center/radius for a job from the backend's own fields
 * (Latitude/Longitude/GeofenceRadiusMeters -- from Jobs.Latitude/Longitude/GeofenceRadiusMeters
 * via WHS_ResolveToken / WHS_GetSites), for Main and Work sites alike. Returns null when the
 * site has no coordinates configured server-side, i.e. nothing to enforce client-side either.
 */
export const getJobGeofenceConfig = (job) => {
  if (!job) return null;
  const latitude = job.latitude ?? job.Latitude;
  const longitude = job.longitude ?? job.Longitude;
  if (latitude === undefined || latitude === null || longitude === undefined || longitude === null) {
    return null;
  }
  return {
    latitude: Number(latitude),
    longitude: Number(longitude),
    radiusMeters: Number(job.geofenceRadiusMeters ?? job.GeofenceRadiusMeters ?? 50),
  };
};

/**
 * Simulated GPS presets for frontend development/testing
 */
export const GPS_PRESETS = {
  INSIDE_HQ: {
    label: "Inside HQ (16m away)",
    coords: {
      latitude: 30.710028,
      longitude: 76.706052,
      accuracy: 5,
    },
  },
  OUTSIDE_HQ: {
    label: "Outside HQ (169m away)",
    coords: {
      latitude: 30.709801,
      longitude: 76.705973,
      accuracy: 10,
    },
  },
};
